import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { Router } from "@angular/router";
import { Chart, registerables } from "chart.js";
import { ChartHelpers } from "src/app/helpers/chartHelpers";
import { DateHelpers } from "src/app/helpers/dateHelpers";
import { NumberOfMonths } from "src/app/models/charts/by-request-type/numberOfMonths.enum";
import { RequestResultsType } from "src/app/models/components/enums/RequestResultsType";
import { WorkflowRequestTypes } from "src/app/models/requests/enums/WorkflowRequestTypes.enum";
import { IWorkflowRequest } from "src/app/models/requests/WorkflowRequest";
import { WorkflowRequestService } from "src/app/services/workflow/WorkflowRequestService";

@Component({
    selector: 'by-request-type',
    templateUrl: './by-request-type.component.html'
})

export class ByRequestTypeComponent implements OnInit, OnChanges {
    @Input() requestType: WorkflowRequestTypes = WorkflowRequestTypes.ServiceAccountDeferment;
    @Input() useWorkflowRequests: boolean = false;
    @Input() workflowRequests: IWorkflowRequest[] | undefined = undefined;
    @Input() allowRequestTypeChange: boolean = false;
    @Input() showDetails: boolean = false;
    @Input() barColor: string = '';
    @Input() numberOfMonths: NumberOfMonths = NumberOfMonths.LastThreeMonths;
    chartId: string = `chart-${Math.random().toString(36).substring(2, 15)}`;
    chart: Chart;
    isLoading: boolean = true;
    requestResults: IWorkflowRequest[] = [];
    get RequestResultsType() { return RequestResultsType; };

    readonly numberOfMonthsOptions = Object.keys(NumberOfMonths)
        .filter((key) => isNaN(Number(key)) && key !== WorkflowRequestTypes.None)
        .map((key) => ({
            displayName: key,
            value: NumberOfMonths[key as keyof typeof NumberOfMonths],
        }))

    readonly workflowRequestTypeKeys = Object.keys(WorkflowRequestTypes)
        .filter((key) => isNaN(Number(key)) && key !== WorkflowRequestTypes.None)
        .map((key) => ({
            displayName: key,
            value: WorkflowRequestTypes[key as keyof typeof WorkflowRequestTypes],
        }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName));

    constructor(private router: Router, private workflowRequestService: WorkflowRequestService) {
        this.setColor();
    }
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.barColor && changes.barColor.currentValue) {
            this.barColor = changes.barColor.currentValue;
        }

        if (changes.useWorkflowRequests && changes.useWorkflowRequests.currentValue) {
            this.useWorkflowRequests = changes.useWorkflowRequests.currentValue;
        }

        if (changes.workflowRequests && changes.workflowRequests.currentValue) {
            this.requestResults = changes.workflowRequests.currentValue;
        }

        if (changes.requestType && changes.requestType.currentValue) {
            this.requestType = changes.requestType.currentValue;
            this.setColor();
        }

        if (this.useWorkflowRequests && this.chartId && this.requestResults) {
            this.setupChart();
        }
    }

    setColor() {
        if (this.barColor == '') {
            this.barColor = ChartHelpers.getBrandColorByWorkflowRequestType(this.requestType)
        }
    }
    ngOnInit(): void {
        Chart.register(...registerables);
        if (!this.useWorkflowRequests) {
            this.getWorkflowRequestData();
        }
        else {
            //we show the placeholder until we get the passed data loaded into the chart
            this.isLoading = true;
        }
    }
    getWorkflowRequestData() {
        this.isLoading = true;
        this.requestResults = [];
        this.workflowRequestService.GetByType(this.requestType, this.numberOfMonths).subscribe({
            next: (response) => {
                this.requestResults = response.data;
                this.setupChart();
            }
        });
    }

    selected(request: IWorkflowRequest) {
        this.router.navigate([`/requests/${request.type}/${request.id}`]);
    }

    loadChartData() {
        let nowdate = DateHelpers.newFirstOfMonthDate(0);
        let monthsBackDates: Date[] = [];
        let monthsBackDatesLabel: string[] = [];
        let monthsBackData: number[] = [];

        for (let i = this.numberOfMonths; i > 0; i--) {
            const date = DateHelpers.newFirstOfMonthDate(i);
            monthsBackDates.push(date);
            monthsBackDatesLabel.push(DateHelpers.monthNames[date.getMonth()]);
        }

        let filteredData = this.requestResults.filter(x => DateHelpers.ensureDate(x.createDate) >= DateHelpers.ensureDate(monthsBackDates[0]) && DateHelpers.ensureDate(x.createDate) < DateHelpers.ensureDate(nowdate));
        let chartData = ChartHelpers.groupByAndCount(filteredData, (item) => DateHelpers.ensureDate(item.createDate).getMonth());


        for (let i = 0; i < this.numberOfMonths; i++) {
            monthsBackData.push(chartData.get(monthsBackDates[i].getMonth()) ?? 0);
        }

        return {
            labels: monthsBackDatesLabel,
            datasets: [{
                label: `# of ${this.requestType == WorkflowRequestTypes.None ? "All" : this.requestType} Requests`,
                data: monthsBackData,
                backgroundColor: [
                    this.barColor,
                ],
            }]
        };

    }
    setupChart() {
        if (!this.chartId) {
            return;
        }
        this.isLoading = true;
        try {
            this.chart?.destroy();

            let data = this.loadChartData();
            this.chart = new Chart(this.chartId, {
                type: 'bar',
                data: data,
                options: {
                    responsive: true,
                    maintainAspectRatio: true,
                    scales: {
                        y: {
                            type: 'linear' as const, // magic
                            ticks: {
                                precision: 0
                            },
                            beginAtZero: true
                        }
                    }
                },
            });
        }
        catch (e) {
        }
        this.isLoading = false;
    }
    onChangeRequestType(event: any) {
        this.requestType = event.value;
        this.setColor();
        this.getWorkflowRequestData();
    }
    onChangeMonthsBack(event: any) {
        this.numberOfMonths = event.value;
        this.getWorkflowRequestData();
    }
}